import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  ShoppingCart,
  Factory,
  Database,
  Download,
  FileSpreadsheet,
  Filter,
  RefreshCw,
  Search,
  Check,
  Tag,
  Edit2,
  Unlink,
  Link,
  Layers,
  Package,
  Trash2,
  BookmarkCheck
} from 'lucide-react';
import * as XLSX from 'xlsx';
import Modal from '../../../components/modal';
import { API_URL } from '../../../utils/constant';
import Swal from 'sweetalert2';

const PurchaseImportPage = () => {
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [reckFormat, setReckFormat] = useState('excel'); // 'excel' | 'pdf'
  const [standardImportType, setStandardImportType] = useState('product'); // 'dispensing' | 'product'
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'matched' | 'unmatched'
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');

  // Product Selection / Change Match Modal state
  const [editingMatchItem, setEditingMatchItem] = useState(null); // { item, index }
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);

  // Auto-Linked Products Log Modal state
  const [showLinksLogModal, setShowLinksLogModal] = useState(false);
  const [linksLogList, setLinksLogList] = useState([]);
  const [linksLogTotal, setLinksLogTotal] = useState(0);
  const [linksLogLoading, setLinksLogLoading] = useState(false);
  const [linksLogSearch, setLinksLogSearch] = useState('');
  const [linksLogVendorFilter, setLinksLogVendorFilter] = useState('all');
  const [linksLogPage, setLinksLogPage] = useState(1);

  const company_id = localStorage.getItem('company_id') || '4';

  const getAcceptedFormats = () => {
    if (selectedVendor === 'reck') {
      return reckFormat === 'excel' ? '.xlsx,.xls' : '.pdf';
    }
    return vendors.find(v => v.id === selectedVendor)?.accept || '';
  };

  const vendors = [
    {
      id: 'reck',
      name: 'Reckeweg',
      description: 'Import Reckeweg PDF or Excel (.xls / .xlsx) Invoice',
      icon: <Factory size={24} />,
      colorClass: 'bg-primary',
      apiEndpoint: `${API_URL}/purchase-upload/reck`,
      verifyEndpoint: `${API_URL}/purchase-upload/reck/verify`,
      matchEndpoint: `${API_URL}/purchase-upload/reck/match-products`,
      accept: '.pdf,.xlsx,.xls'
    },
    {
      id: 'wsi',
      name: 'WSI',
      description: 'Import WSI PDF Invoice',
      icon: <ShoppingCart size={24} />,
      colorClass: 'bg-info',
      apiEndpoint: `${API_URL}/purchase-upload/wsi`,
      verifyEndpoint: `${API_URL}/purchase-upload/wsi/verify`,
      matchEndpoint: `${API_URL}/purchase-upload/wsi/match-products`,
      accept: '.pdf'
    },
    {
      id: 'modern',
      name: 'Modern Homoeo',
      description: 'Import Modern Homoeo Image, PDF, or Excel Invoice',
      icon: <ImageIcon size={24} />,
      colorClass: 'bg-danger',
      apiEndpoint: `${API_URL}/purchase-upload/modern-homoeo`,
      verifyEndpoint: `${API_URL}/purchase-upload/modern-homoeo/verify`,
      matchEndpoint: `${API_URL}/purchase-upload/modern-homoeo/match-products`,
      accept: '.pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls'
    },
    {
      id: 'mani',
      name: 'Mani Enterprise',
      description: 'Import Mani Enterprise Image, PDF, or Excel Invoice',
      icon: <Database size={24} />,
      colorClass: 'bg-success',
      apiEndpoint: `${API_URL}/purchase-upload/mani-enterprise`,
      verifyEndpoint: `${API_URL}/purchase-upload/mani-enterprise/verify`,
      matchEndpoint: `${API_URL}/purchase-upload/mani-enterprise/match-products`,
      accept: '.pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls'
    },
    {
      id: 'standard',
      name: 'Other / Local Purchase',
      description: 'Import Standard Excel with Type selection (Dispensing Materials vs Other Products)',
      icon: <Package size={24} />,
      colorClass: 'bg-warning text-dark',
      apiEndpoint: `${API_URL}/purchase-upload/standard`,
      verifyEndpoint: `${API_URL}/purchase-upload/standard/verify`,
      matchEndpoint: `${API_URL}/purchase-upload/standard/match-products`,
      accept: '.xlsx,.xls'
    }
  ];

  // Fetch categories for company
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/raw-categories/${company_id}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setCategories(data);
          }
        }
      } catch (err) {
        console.warn('Could not load categories list:', err);
      }
    };
    fetchCategories();
  }, [company_id]);

  const handleFileSelect = (file) => {
    setUploadedFile(file);
    setError(null);
    setResult(null);
    setVerifiedData(null);
    setSearchTerm('');
    setFilterStatus('all');
  };

  const handleProceed = async (createMissing = false) => {
    if (!uploadedFile || !selectedVendor) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('company_id', company_id);
      formData.append('create_missing_products', createMissing);
      formData.append('import_type', selectedVendor === 'standard' ? standardImportType : 'product');

      // If we already verified and user confirmed import, send updated items JSON
      if (verifiedData && verifiedData.items) {
        formData.append('items', JSON.stringify(verifiedData.items));
      }

      const vendorConfig = vendors.find(v => v.id === selectedVendor);
      const isVerify = !verifiedData;
      const apiEndpoint = isVerify
        ? (vendorConfig?.verifyEndpoint || `${API_URL}/purchase-upload/${selectedVendor}/verify`)
        : (vendorConfig?.apiEndpoint || `${API_URL}/purchase-upload/${selectedVendor}`);

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Operation failed');
      }

      if (isVerify) {
        setVerifiedData(data.data);
        if (data.data.categories && data.data.categories.length > 0) {
          setCategories(data.data.categories);
        }
        Swal.fire({
          icon: 'success',
          title: 'Verification Complete',
          text: `Found ${data.data.total_items} items in invoice (${data.data.matched_items} matched, ${data.data.unmatched_items} unmatched). You can adjust products, categories and batch numbers below before importing.`,
          timer: 3500
        });
      } else {
        setResult(data.data);
        setVerifiedData(null);
        Swal.fire({
          icon: 'success',
          title: 'Import Successful',
          text: `Processed ${data.data.items_imported || data.data.total_found || data.data.total_imported || 0} items successfully.`,
          timer: 3000
        });
      }
    } catch (err) {
      setError(err.message);
      Swal.fire({ icon: 'error', title: 'Operation Failed', text: err.message });
    } finally {
      setIsProcessing(false);
    }
  };

  // Re-match when user changes an item's category
  const handleCategoryChange = async (indexInFullList, newCategory) => {
    if (!verifiedData || !verifiedData.items) return;

    const updatedItems = [...verifiedData.items];
    const catObj = categories.find(c => c.name === newCategory || String(c.id) === String(newCategory));
    
    updatedItems[indexInFullList] = {
      ...updatedItems[indexInFullList],
      category: catObj ? catObj.name : newCategory,
      category_id: catObj ? catObj.id : null
    };

    // Optimistically update
    setVerifiedData(prev => ({
      ...prev,
      items: updatedItems
    }));

    // Trigger re-match on backend
    setIsMatching(true);
    try {
      const vendorConfig = vendors.find(v => v.id === selectedVendor);
      const matchEndpoint = vendorConfig?.matchEndpoint || `${API_URL}/purchase-upload/${selectedVendor}/match-products`;

      const response = await fetch(matchEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: updatedItems,
          company_id,
          import_type: selectedVendor === 'standard' ? standardImportType : 'product'
        })
      });

      const resData = await response.json();
      if (response.ok && resData.data) {
        setVerifiedData(prev => ({
          ...prev,
          matched_items: resData.data.matched_items,
          unmatched_items: resData.data.unmatched_items,
          items: resData.data.items
        }));
      }
    } catch (err) {
      console.error('Error re-matching products:', err);
    } finally {
      setIsMatching(false);
    }
  };

  // Handle manual batch number edit
  const handleBatchChange = (indexInFullList, newBatch) => {
    if (!verifiedData || !verifiedData.items) return;

    const updatedItems = [...verifiedData.items];
    updatedItems[indexInFullList] = {
      ...updatedItems[indexInFullList],
      batch_no: newBatch
    };

    setVerifiedData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const searchTimerRef = useRef(null);

  // Open Product Match modal for a specific line item
  const openProductMatchModal = (item, indexInFullList) => {
    setEditingMatchItem({ item, index: indexInFullList });
    
    // Clean initial query: strip attached potencies (e.g. "Sulphur 30CH" -> "Sulphur")
    let initialQuery = item.matchedProductName || item.name || '';
    initialQuery = initialQuery
      .replace(/\b(\d+X|\d+D|\d+C|\d+CH|\d+M|CM|MT|Q|LM\s*\d*|0\/\d+|\d+)\s*(?:CH|C|D|X|ML|GM|TAB|TABLETS|DROPS|SYRUP|OINTMENT|DILUTION|DIL)?\b/gi, '')
      .replace(/\b(CH|C|DIL|DILUTION|LP|LOW\s+POT|RN|BC|TABS|TABLETS|DROPS|OINTMENT|SYRUP|CREAM|GEL)\b/gi, '')
      .replace(/[^a-zA-Z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!initialQuery) initialQuery = item.name || '';

    setProductSearchQuery(initialQuery);
    fetchProductSearchResults(initialQuery, true);
  };

  // Search products from backend with debouncing
  const fetchProductSearchResults = (query, immediate = false) => {
    setIsSearchingProducts(true);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    const executeSearch = async () => {
      try {
        const importTypeParam = (selectedVendor === 'standard' && standardImportType === 'dispensing') ? '&import_type=dispensing' : '';
        const response = await fetch(`${API_URL}/purchase-upload/search-products/${company_id}?q=${encodeURIComponent(query || '')}${importTypeParam}`);
        if (response.ok) {
          const data = await response.json();
          setProductSearchResults(data.data || []);
        } else {
          setProductSearchResults([]);
        }
      } catch (err) {
        console.error('Error searching products:', err);
        setProductSearchResults([]);
      } finally {
        setIsSearchingProducts(false);
      }
    };

    if (immediate) {
      executeSearch();
    } else {
      searchTimerRef.current = setTimeout(executeSearch, 250);
    }
  };

  // Fetch linked products log
  const fetchLinksLogList = async (page = 1, search = '', vendor = 'all') => {
    setLinksLogLoading(true);
    try {
      const vendorParam = vendor && vendor !== 'all' ? `&vendor_key=${vendor}` : '';
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`${API_URL}/purchase-upload/product-links/${company_id}?page=${page}&limit=50${vendorParam}${searchParam}`);
      if (response.ok) {
        const data = await response.json();
        setLinksLogList(data.data?.links || []);
        setLinksLogTotal(data.data?.total || 0);
        setLinksLogPage(page);
      }
    } catch (err) {
      console.error('Failed to fetch linked products log:', err);
    } finally {
      setLinksLogLoading(false);
    }
  };

  const handleDeleteLinkLog = async (id, itemName) => {
    const confirm = await Swal.fire({
      title: 'Remove Linked Product?',
      text: `Remove saved link for "${itemName}"? Next time it will appear as unmatched until manually linked again.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
      confirmButtonColor: '#dc3545'
    });
    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}/purchase-upload/product-links/${id}?company_id=${company_id}`, {
          method: 'DELETE'
        });
        if (res.ok) {
          Swal.fire({
            icon: 'success',
            title: 'Removed',
            text: 'Product link removed from auto-link log.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000
          });
          fetchLinksLogList(linksLogPage, linksLogSearch, linksLogVendorFilter);
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    }
  };

  // Assign user-selected product to the row and auto-save mapping to log
  const handleSelectProductMatch = (indexInFullList, product) => {
    if (!verifiedData || !verifiedData.items) return;

    const targetItem = verifiedData.items[indexInFullList];
    const updatedItems = [...verifiedData.items];
    updatedItems[indexInFullList] = {
      ...updatedItems[indexInFullList],
      matched: true,
      product_id: product.id,
      matchedProductName: product.name,
      category_id: product.category_id || updatedItems[indexInFullList].category_id,
      linkedFromLog: true
    };

    const newMatchedCount = updatedItems.filter(i => i.matched).length;

    setVerifiedData(prev => ({
      ...prev,
      items: updatedItems,
      matched_items: newMatchedCount,
      unmatched_items: updatedItems.length - newMatchedCount
    }));

    setEditingMatchItem(null);

    // Persist link log to backend immediately
    if (targetItem && targetItem.name) {
      fetch(`${API_URL}/purchase-upload/save-product-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id,
          vendor_key: selectedVendor,
          vendor_name: vendors.find(v => v.id === selectedVendor)?.name,
          import_item_name: targetItem.name,
          import_item_code: targetItem.code || targetItem.art_no || null,
          product_id: product.id,
          product_name: product.name,
          category_id: product.category_id || targetItem.category_id,
          category_name: targetItem.category
        })
      }).catch(e => console.warn('Could not save product link log:', e));
    }

    Swal.fire({
      icon: 'success',
      title: 'Product Linked & Logged',
      text: `Successfully linked with "${product.name}" (ID: #${product.id}). Future imports will automatically link to this product.`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true
    });
  };

  // Unlink / Clear product match for the row and remove from auto-link log
  const handleClearProductMatch = (indexInFullList) => {
    if (!verifiedData || !verifiedData.items) return;

    const targetItem = verifiedData.items[indexInFullList];
    const updatedItems = [...verifiedData.items];
    updatedItems[indexInFullList] = {
      ...updatedItems[indexInFullList],
      matched: false,
      product_id: null,
      matchedProductName: null,
      linkedFromLog: false
    };

    const newMatchedCount = updatedItems.filter(i => i.matched).length;

    setVerifiedData(prev => ({
      ...prev,
      items: updatedItems,
      matched_items: newMatchedCount,
      unmatched_items: updatedItems.length - newMatchedCount
    }));

    setEditingMatchItem(null);

    // Remove from link log in backend
    if (targetItem && targetItem.name) {
      fetch(`${API_URL}/purchase-upload/remove-product-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id,
          vendor_key: selectedVendor,
          import_item_name: targetItem.name
        })
      }).catch(e => console.warn('Could not remove product link log:', e));
    }

    Swal.fire({
      icon: 'info',
      title: 'Product Unlinked',
      text: `Removed match. Item marked as blank / unmatched and removed from auto-link log.`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true
    });
  };

  // Apply a category in bulk to all currently unmatched items
  const handleBulkApplyCategory = async () => {
    if (!bulkCategory || !verifiedData || !verifiedData.items) return;

    const catObj = categories.find(c => c.name === bulkCategory || String(c.id) === String(bulkCategory));
    const targetCatName = catObj ? catObj.name : bulkCategory;
    const targetCatId = catObj ? catObj.id : null;

    const updatedItems = verifiedData.items.map(item => {
      if (!item.matched) {
        return {
          ...item,
          category: targetCatName,
          category_id: targetCatId
        };
      }
      return item;
    });

    setVerifiedData(prev => ({
      ...prev,
      items: updatedItems
    }));

    setIsMatching(true);
    try {
      const vendorConfig = vendors.find(v => v.id === selectedVendor);
      const matchEndpoint = vendorConfig?.matchEndpoint || `${API_URL}/purchase-upload/${selectedVendor}/match-products`;

      const response = await fetch(matchEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: updatedItems,
          company_id,
          import_type: selectedVendor === 'standard' ? standardImportType : 'product'
        })
      });

      const resData = await response.json();
      if (response.ok && resData.data) {
        setVerifiedData(prev => ({
          ...prev,
          matched_items: resData.data.matched_items,
          unmatched_items: resData.data.unmatched_items,
          items: resData.data.items
        }));
        Swal.fire({
          icon: 'success',
          title: 'Bulk Category Applied',
          text: `Applied "${targetCatName}" to unmatched items. (${resData.data.matched_items} matched)`,
          timer: 2000
        });
      }
    } catch (err) {
      console.error('Error applying bulk category:', err);
    } finally {
      setIsMatching(false);
    }
  };

  // Export Unmatched Items to Excel
  const downloadUnmatchedExcel = (itemsList, invoiceNo) => {
    if (!itemsList || itemsList.length === 0) {
      Swal.fire({ icon: 'info', title: 'No Unmatched Items', text: 'All items have been matched with the database.' });
      return;
    }

    const excelRows = itemsList.map((item, idx) => ({
      '#': idx + 1,
      'Product Code': item.code || '',
      'Product Name': item.name || '',
      'Potency': item.potency || '',
      'Volume': item.volume || '',
      'Selected Category': item.category || 'Unknown',
      'HSN': item.hsn || '',
      'Batch No': item.batch_no || '',
      'Expiry Date': item.exp_date || '',
      'Quantity': item.qty || 1,
      'Rate (DP)': item.rate ? Number(item.rate).toFixed(2) : '0.00',
      'MRP': item.mrp ? Number(item.mrp).toFixed(2) : '0.00',
      'Total Amount': item.amount ? Number(item.amount).toFixed(2) : (item.qty * item.rate).toFixed(2),
      'Database Status': item.matched ? `Matched (ID: ${item.product_id})` : 'Unmatched'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unmatched_Products');

    // Auto-fit column widths
    const colWidths = [
      { wch: 5 },  // #
      { wch: 15 }, // Code
      { wch: 35 }, // Name
      { wch: 10 }, // Potency
      { wch: 12 }, // Volume
      { wch: 20 }, // Category
      { wch: 12 }, // HSN
      { wch: 15 }, // Batch
      { wch: 12 }, // Exp
      { wch: 8 },  // Qty
      { wch: 12 }, // Rate
      { wch: 12 }, // MRP
      { wch: 14 }, // Amount
      { wch: 22 }  // Status
    ];
    worksheet['!cols'] = colWidths;

    const vendorPrefix = selectedVendor === 'reck' ? 'Reckeweg' : (selectedVendor === 'wsi' ? 'WSI' : (selectedVendor === 'modern' ? 'Modern_Homoeo' : (selectedVendor === 'mani' ? 'Mani_Enterprise' : 'Purchase')));
    const fileName = `${vendorPrefix}_Unmatched_Products_${invoiceNo || 'Invoice'}_${Date.now()}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: 'success',
      title: 'Excel Downloaded',
      text: `Exported ${itemsList.length} unmatched products to ${fileName}`,
      timer: 2500
    });
  };

  // Export All Items to Excel
  const downloadAllItemsExcel = (itemsList, invoiceNo) => {
    if (!itemsList || itemsList.length === 0) return;

    const excelRows = itemsList.map((item, idx) => ({
      '#': idx + 1,
      'Product Code': item.code || '',
      'Product Name': item.name || '',
      'Potency': item.potency || '',
      'Volume': item.volume || '',
      'Category': item.category || 'Unknown',
      'HSN': item.hsn || '',
      'Batch No': item.batch_no || '',
      'Expiry Date': item.exp_date || '',
      'Quantity': item.qty || 1,
      'Rate': item.rate ? Number(item.rate).toFixed(2) : '0.00',
      'MRP': item.mrp ? Number(item.mrp).toFixed(2) : '0.00',
      'Amount': item.amount ? Number(item.amount).toFixed(2) : '0.00',
      'Database Match': item.matched ? 'YES' : 'NO',
      'Matched Product ID': item.product_id || '-',
      'Matched DB Name': item.matchedProductName || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All_Items');

    const vendorPrefix = selectedVendor === 'reck' ? 'Reckeweg' : (selectedVendor === 'wsi' ? 'WSI' : (selectedVendor === 'modern' ? 'Modern_Homoeo' : (selectedVendor === 'mani' ? 'Mani_Enterprise' : 'Purchase')));
    const fileName = `${vendorPrefix}_Invoice_Items_${invoiceNo || 'Invoice'}_${Date.now()}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleConfirmImport = () => {
    if (verifiedData && verifiedData.unmatched_items > 0) {
      const itemLabel = (selectedVendor === 'standard' && standardImportType === 'dispensing')
        ? 'dispensing materials in the inventory'
        : 'products in the database under their selected categories';
      Swal.fire({
        title: 'Unmatched Items Detected',
        text: `There are ${verifiedData.unmatched_items} unmatched items. Would you like to automatically create these ${itemLabel} during import?`,
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Yes, Create & Import',
        denyButtonText: 'No, Import As-Is',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#198754',
        denyButtonColor: '#0dcaf0',
      }).then((res) => {
        if (res.isConfirmed) {
          handleProceed(true);
        } else if (res.isDenied) {
          handleProceed(false);
        }
      });
    } else {
      handleProceed(false);
    }
  };

  const reset = () => {
    setUploadedFile(null);
    setResult(null);
    setError(null);
    setVerifiedData(null);
    setSearchTerm('');
    setFilterStatus('all');
    setBulkCategory('');
    setEditingMatchItem(null);
  };

  // Filtered items list for preview table
  const displayedItems = verifiedData?.items ? verifiedData.items.map((item, originalIndex) => ({
    ...item,
    originalIndex
  })).filter(item => {
    if (filterStatus === 'matched' && !item.matched) return false;
    if (filterStatus === 'unmatched' && item.matched) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const nameMatch = (item.name || '').toLowerCase().includes(q);
      const catMatch = (item.category || '').toLowerCase().includes(q);
      const potencyMatch = (item.potency || '').toLowerCase().includes(q);
      const batchMatch = (item.batch_no || '').toLowerCase().includes(q);
      const matchedNameMatch = (item.matchedProductName || '').toLowerCase().includes(q);
      const codeMatch = (item.code || '').toLowerCase().includes(q);
      const brandMatch = (item.brand || '').toLowerCase().includes(q);
      return nameMatch || catMatch || potencyMatch || batchMatch || matchedNameMatch || codeMatch || brandMatch;
    }
    return true;
  }) : [];

  const unmatchedList = verifiedData?.items?.filter(item => !item.matched) || [];

  return (
    <div className="container-fluid py-4 px-md-5">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h1 className="display-6 fw-bold text-dark mb-1">Purchase Invoice Import</h1>
          <p className="text-muted small mb-0">Upload PDF, Image, or Excel invoices, verify product categories & batch numbers, and link directly to your inventory</p>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-outline-primary d-inline-flex align-items-center gap-2 shadow-sm fw-semibold px-3 py-2"
            onClick={() => {
              setShowLinksLogModal(true);
              fetchLinksLogList(1, '', selectedVendor || 'all');
            }}
          >
            <Database size={16} />
            <span>Auto-Linked Products Log</span>
            {linksLogTotal > 0 && (
              <span className="badge bg-primary text-white rounded-pill ms-1">{linksLogTotal}</span>
            )}
          </button>
        </div>
      </div>

      {!selectedVendor ? (
        <div className="row g-4 justify-content-center">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="col-12 col-md-6 col-lg-4">
              <div
                className="card h-100 shadow-sm border-0 vendor-card text-center p-3"
                onClick={() => setSelectedVendor(vendor.id)}
                style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
              >
                <div className="card-body">
                  <div className={`d-inline-flex p-3 rounded-circle text-white mb-3 ${vendor.colorClass}`}>
                    {vendor.icon}
                  </div>
                  <h5 className="card-title fw-bold">{vendor.name}</h5>
                  <p className="card-text text-muted small">{vendor.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card shadow-lg border-0">
          <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3 px-4">
            <h5 className="mb-0 fw-bold text-white d-flex align-items-center gap-2">
              <span>Importing for {vendors.find(v => v.id === selectedVendor)?.name}</span>
              <span className="badge bg-info text-dark font-monospace" style={{ fontSize: '11px' }}>
                Smart Category & Batch Matching
              </span>
            </h5>
            <button className="btn btn-link text-white p-0" onClick={() => { setSelectedVendor(null); reset(); }}>
              <X size={24} />
            </button>
          </div>

          <div className="card-body p-4 p-md-5">
            {/* Reckeweg File Format Switcher */}
            {selectedVendor === 'reck' && !result && !verifiedData && (
              <div className="d-flex justify-content-center mb-4">
                <div className="btn-group p-1 bg-light rounded-pill border shadow-sm">
                  <button
                    type="button"
                    className={`btn btn-sm px-4 py-2 rounded-pill fw-bold ${reckFormat === 'excel' ? 'btn-primary text-white shadow-sm' : 'btn-light text-secondary'}`}
                    onClick={() => { setReckFormat('excel'); setUploadedFile(null); setVerifiedData(null); }}
                  >
                    <FileSpreadsheet size={16} className="me-2" />
                    Excel Spreadsheet (.xlsx / .xls)
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm px-4 py-2 rounded-pill fw-bold ${reckFormat === 'pdf' ? 'btn-primary text-white shadow-sm' : 'btn-light text-secondary'}`}
                    onClick={() => { setReckFormat('pdf'); setUploadedFile(null); setVerifiedData(null); }}
                  >
                    <Upload size={16} className="me-2" />
                    PDF Document (.pdf)
                  </button>
                </div>
              </div>
            )}

            {/* Standard / Other Purchase Type Switcher */}
            {selectedVendor === 'standard' && !result && !verifiedData && (
              <div className="d-flex flex-column align-items-center mb-4">
                <div className="text-muted small fw-semibold mb-2">Select Item Type for this Purchase Invoice:</div>
                <div className="btn-group p-1 bg-light rounded-pill border shadow-sm">
                  <button
                    type="button"
                    className={`btn btn-sm px-4 py-2 rounded-pill fw-bold ${standardImportType === 'dispensing' ? 'btn-success text-white shadow-sm' : 'btn-light text-secondary'}`}
                    onClick={() => { setStandardImportType('dispensing'); setUploadedFile(null); setVerifiedData(null); }}
                  >
                    <Layers size={16} className="me-2" />
                    🧪 Dispensing Products (Dispense Materials)
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm px-4 py-2 rounded-pill fw-bold ${standardImportType === 'product' ? 'btn-primary text-white shadow-sm' : 'btn-light text-secondary'}`}
                    onClick={() => { setStandardImportType('product'); setUploadedFile(null); setVerifiedData(null); }}
                  >
                    <Package size={16} className="me-2" />
                    📦 Other Products (Medicines / General Goods)
                  </button>
                </div>
                <small className="text-muted mt-2 text-center" style={{ maxWidth: '540px', fontSize: '12px' }}>
                  {standardImportType === 'dispensing'
                    ? '💡 Stock will arrive as Fresh Stock and can be allocated to Dispensing Materials inventory from the Allocate Stock screen.'
                    : '💡 Stock will be recorded in Purchases Products inventory with support for Purchases Stock batch allocation.'}
                </small>
              </div>
            )}

            {!result && !verifiedData && (
              <div
                className={`upload-zone border border-2 border-dashed rounded-4 p-5 text-center ${isDragging ? 'bg-light border-primary' : 'bg-light'}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]); }}
                style={{ position: 'relative' }}
              >
                <input
                  type="file"
                  className="position-absolute w-100 h-100 opacity-0 top-0 start-0 cursor-pointer"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  accept={getAcceptedFormats()}
                />
                <div className="py-4">
                  {selectedVendor === 'standard' || (selectedVendor === 'reck' && reckFormat === 'excel') ? (
                    <FileSpreadsheet size={48} className="text-success mb-3" />
                  ) : (
                    <Upload size={48} className="text-secondary mb-3" />
                  )}
                  <h4 className="fw-bold">{uploadedFile ? uploadedFile.name : `Click or Drag ${selectedVendor === 'standard' || (selectedVendor === 'reck' && reckFormat === 'excel') ? 'Excel File' : 'Invoice File'} Here`}</h4>
                  <p className="text-muted mb-0">
                    {uploadedFile ? `${(uploadedFile.size / 1024).toFixed(2)} KB` : `Accepted format: ${getAcceptedFormats()}`}
                  </p>
                </div>
              </div>
            )}

            {uploadedFile && !result && !verifiedData && !isProcessing && (
              <div className="mt-4 d-flex gap-2">
                <button className="btn btn-primary btn-lg flex-grow-1 fw-bold" onClick={() => handleProceed(false)}>
                  Verify Invoice & Check Categories
                </button>
                <button className="btn btn-outline-secondary btn-lg" onClick={reset}>
                  Cancel
                </button>
              </div>
            )}

            {/* Verified Data Table with Category Selector & Excel Export */}
            {verifiedData && !result && !isProcessing && (
              <div className="mt-2">
                {/* Summary Alert Bar */}
                <div className="alert alert-info py-3 px-3 mb-4 rounded-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="text-start">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h6 className="fw-bold mb-0 text-primary">Invoice Verification Overview</h6>
                      {selectedVendor === 'standard' && (
                        <span className={`badge ${standardImportType === 'dispensing' ? 'bg-success' : 'bg-primary'} text-white`}>
                          {standardImportType === 'dispensing' ? '🧪 Dispensing Material' : '📦 Product Import'}
                        </span>
                      )}
                    </div>
                    <small className="text-muted">
                      {verifiedData.supplier_name && <span>Supplier: <strong>{verifiedData.supplier_name}</strong> | </span>}
                      Invoice No: <strong>{verifiedData.invoice_no || '-'}</strong> | Date: <strong>{verifiedData.invoice_date ? new Date(verifiedData.invoice_date).toLocaleDateString('en-IN') : '-'}</strong> | Total Items: <strong>{verifiedData.total_items}</strong>
                    </small>
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="badge bg-success text-white px-3 py-2 fw-semibold">
                      Matched: {verifiedData.matched_items}
                    </span>
                    <span className="badge bg-danger text-white px-3 py-2 fw-semibold">
                      Unmatched: {verifiedData.unmatched_items}
                    </span>

                    {unmatchedList.length > 0 && (
                      <button
                        className="btn btn-warning btn-sm fw-bold d-flex align-items-center gap-1 shadow-sm ms-2"
                        onClick={() => downloadUnmatchedExcel(unmatchedList, verifiedData.invoice_no)}
                        title="Download unmatched products in Excel"
                      >
                        <FileSpreadsheet size={16} />
                        Download Unmatched Excel ({unmatchedList.length})
                      </button>
                    )}

                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                      onClick={() => downloadAllItemsExcel(verifiedData.items, verifiedData.invoice_no)}
                      title="Download complete invoice items to Excel"
                    >
                      <Download size={15} />
                      Export All
                    </button>

                    <button
                      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 shadow-sm fw-semibold"
                      onClick={() => {
                        setShowLinksLogModal(true);
                        fetchLinksLogList(1, '', selectedVendor || 'all');
                      }}
                      title="View & manage auto-linked products log"
                    >
                      <Database size={15} />
                      Linked Products Log
                    </button>
                  </div>
                </div>

                {/* Filter and Bulk Category Assignment Toolbar */}
                <div className="row g-2 mb-3 align-items-center bg-light p-3 rounded-3 border">
                  {/* Search */}
                  <div className="col-12 col-md-4">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white"><Search size={14} /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, potency, batch, category..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button className="btn btn-outline-secondary" onClick={() => setSearchTerm('')}>
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filter Status Buttons */}
                  <div className="col-12 col-md-4 d-flex gap-1 justify-content-md-center">
                    <button
                      className={`btn btn-sm ${filterStatus === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
                      onClick={() => setFilterStatus('all')}
                    >
                      All ({verifiedData.items?.length || 0})
                    </button>
                    <button
                      className={`btn btn-sm ${filterStatus === 'matched' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setFilterStatus('matched')}
                    >
                      Matched ({verifiedData.matched_items || 0})
                    </button>
                    <button
                      className={`btn btn-sm ${filterStatus === 'unmatched' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setFilterStatus('unmatched')}
                    >
                      Unmatched ({verifiedData.unmatched_items || 0})
                    </button>
                  </div>

                  {/* Bulk Category Selector */}
                  <div className="col-12 col-md-4">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white text-muted"><Tag size={13} /></span>
                      <select
                        className="form-select"
                        value={bulkCategory}
                        onChange={(e) => setBulkCategory(e.target.value)}
                      >
                        <option value="">-- Bulk Set Category --</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                      <button
                        className="btn btn-secondary"
                        type="button"
                        disabled={!bulkCategory || isMatching}
                        onClick={handleBulkApplyCategory}
                        title="Apply category to all unmatched items"
                      >
                        Apply to Unmatched
                      </button>
                    </div>
                  </div>
                </div>

                {isMatching && (
                  <div className="text-center py-2 mb-2 text-primary d-flex align-items-center justify-content-center gap-2">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    <small className="fw-semibold">Re-evaluating database matching for selected categories...</small>
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive rounded border border-light-subtle shadow-sm mb-4" style={{ maxHeight: '520px', overflowY: 'auto' }}>
                  <table className="table table-hover align-middle mb-0 text-start" style={{ fontSize: '13px' }}>
                    <thead className="table-light sticky-top shadow-sm" style={{ zIndex: 1 }}>
                      <tr>
                        <th style={{ width: '40px' }}>#</th>
                        <th style={{ minWidth: '220px' }}>Invoice Item</th>
                        <th style={{ minWidth: '240px' }}>Matched DB Product (Click to Change)</th>
                        <th style={{ minWidth: '160px' }}>Category</th>
                        <th style={{ width: '130px' }}>Batch No</th>
                        <th className="text-center" style={{ width: '70px' }}>Qty</th>
                        <th className="text-end" style={{ width: '120px' }}>Rate / MRP</th>
                        <th className="text-end" style={{ width: '110px' }}>Total Amount</th>
                        <th className="text-center" style={{ width: '100px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedItems.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">
                            No products match the search / filter criteria.
                          </td>
                        </tr>
                      ) : (
                        displayedItems.map((item) => (
                          <tr key={item.originalIndex} className={item.matched ? '' : 'table-warning bg-opacity-25'}>
                            <td className="text-muted small fw-bold">{item.originalIndex + 1}</td>
                            
                            {/* Invoice Item Column */}
                            <td>
                              <div className="fw-bold text-dark">
                                {item.name}
                              </div>
                              <div className="d-flex flex-wrap gap-1 align-items-center mt-1">
                                {item.brand && (
                                  <span className="badge bg-primary-subtle text-primary border" style={{ fontSize: '10px' }}>
                                    Brand: {item.brand}
                                  </span>
                                )}
                                {item.code && (
                                  <span className="badge bg-light text-secondary border font-monospace" style={{ fontSize: '10px' }}>
                                    {item.code}
                                  </span>
                                )}
                                {item.potency && item.potency !== 'Standard' && (
                                  <span className="badge bg-info-subtle text-info border" style={{ fontSize: '10px' }}>
                                    Pot: {item.potency}
                                  </span>
                                )}
                                {item.volume && item.volume !== 'Standard' && (
                                  <span className="badge bg-secondary-subtle text-secondary border" style={{ fontSize: '10px' }}>
                                    Vol: {item.volume}
                                  </span>
                                )}
                                {item.unit && (
                                  <span className="badge bg-secondary-subtle text-secondary border" style={{ fontSize: '10px' }}>
                                    Unit: {item.unit}
                                  </span>
                                )}
                                {item.pack_size && item.pack_size !== '1' && item.pack_size !== 1 && (
                                  <span className="badge bg-warning-subtle text-warning-emphasis border" style={{ fontSize: '10px' }}>
                                    Pack: {item.pack_size}
                                  </span>
                                )}
                              </div>
                              <small className="text-muted d-block mt-0.5" style={{ fontSize: '11px' }}>
                                HSN: {item.hsn || '-'} {item.exp_date ? `| Exp: ${item.exp_date}` : ''}
                              </small>
                            </td>

                            {/* Matched DB Product (Clickable / Editable) */}
                            <td>
                              {item.matched && item.product_id ? (
                                <div className="p-2 rounded-2 bg-success-subtle border border-success-subtle d-flex align-items-start justify-content-between gap-2">
                                  <div>
                                    <div className="fw-bold text-success" style={{ fontSize: '12px', lineHeight: '1.3' }}>
                                      {item.matchedProductName || item.name}
                                    </div>
                                    <div className="d-flex gap-1 align-items-center mt-1">
                                      <span className="badge bg-success text-white font-monospace" style={{ fontSize: '10px' }}>
                                        ID: #{item.product_id}
                                      </span>
                                      {item.linkedFromLog && (
                                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle d-inline-flex align-items-center gap-1" style={{ fontSize: '10px' }} title="Automatically matched from previous link log">
                                          <Link size={10} /> Auto-Linked
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-success p-1 py-0 shadow-sm"
                                      title="Change Matched Product"
                                      onClick={() => openProductMatchModal(item, item.originalIndex)}
                                      style={{ fontSize: '11px' }}
                                    >
                                      <Edit2 size={12} className="me-1" />
                                      Change
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger p-1 py-0 shadow-sm"
                                      title="Unlink Product (Mark as Blank)"
                                      onClick={() => handleClearProductMatch(item.originalIndex)}
                                      style={{ fontSize: '11px' }}
                                    >
                                      <Unlink size={12} />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-2 rounded-2 bg-light border border-dashed border-secondary-subtle d-flex align-items-center justify-content-between gap-2">
                                  <span className="text-muted fst-italic small">
                                    -- Blank (No Product Linked) --
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary py-0 px-2 fw-semibold d-flex align-items-center gap-1 shadow-sm"
                                    onClick={() => openProductMatchModal(item, item.originalIndex)}
                                    style={{ fontSize: '11px' }}
                                  >
                                    <Link size={12} />
                                    Select Product
                                  </button>
                                </div>
                              )}
                            </td>

                            {/* Category Selector Dropdown */}
                            <td>
                              <select
                                className="form-select form-select-sm border-secondary-subtle fw-semibold"
                                value={item.category || ''}
                                onChange={(e) => handleCategoryChange(item.originalIndex, e.target.value)}
                                style={{ fontSize: '12px' }}
                              >
                                {categories.length > 0 ? (
                                  categories.map((cat) => (
                                    <option key={cat.id} value={cat.name}>
                                      {cat.name}
                                    </option>
                                  ))
                                ) : (
                                  <>
                                    <option value={item.category || 'MT'}>{item.category || 'MT'}</option>
                                    <option value="Dilution(Others)">Dilution(Others)</option>
                                    <option value="Dilution(FM)">Dilution(FM)</option>
                                    <option value="Biochemics">Biochemics</option>
                                    <option value="Biocombinations">Biocombinations</option>
                                    <option value="SPECIALITIES">SPECIALITIES</option>
                                    <option value="RN">RN</option>
                                    <option value="SYRUP">SYRUP</option>
                                    <option value="LATT">LATT</option>
                                    <option value="LM Potencies">LM Potencies</option>
                                  </>
                                )}
                              </select>
                            </td>

                            {/* Batch No Input */}
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm font-monospace border-secondary-subtle"
                                placeholder="Batch No"
                                value={item.batch_no || ''}
                                onChange={(e) => handleBatchChange(item.originalIndex, e.target.value)}
                                style={{ fontSize: '12px' }}
                              />
                            </td>

                            <td className="text-center fw-bold">{item.qty}</td>
                            <td className="text-end">
                              <div className="fw-bold">Rate: ₹{Number(item.rate || 0).toFixed(2)}</div>
                              <small className="text-muted d-block">MRP: ₹{Number(item.mrp || item.rate || 0).toFixed(2)}</small>
                              {item.mrp > item.rate && item.rate > 0 && (
                                <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '10px' }}>
                                  Disc: {(((item.mrp - item.rate) / item.mrp) * 100).toFixed(1)}% (₹{(item.mrp - item.rate).toFixed(2)})
                                </span>
                              )}
                            </td>
                            <td className="text-end fw-semibold">₹{Number(item.amount || (item.qty * item.rate) || 0).toFixed(2)}</td>

                            {/* Status Column */}
                            <td className="text-center">
                              {item.matched ? (
                                item.linkedFromLog ? (
                                  <span className="badge bg-primary text-white px-2 py-1 fs-11 d-inline-flex align-items-center gap-1" title="Already linked from previous link log">
                                    <Link size={12} /> Log Matched
                                  </span>
                                ) : (
                                  <span className="badge bg-success text-white px-2 py-1 fs-11 d-inline-flex align-items-center gap-1">
                                    <Check size={12} /> Matched
                                  </span>
                                )
                              ) : (
                                <span className="badge bg-warning text-dark px-2 py-1 fs-11">
                                  Unmatched
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button className="btn btn-success btn-lg flex-grow-1 fw-bold py-2.5 shadow-sm" onClick={handleConfirmImport}>
                    Confirm & Complete Import ({verifiedData.items?.length || 0} Items)
                  </button>

                  {unmatchedList.length > 0 && (
                    <button
                      className="btn btn-outline-warning btn-lg fw-bold d-flex align-items-center gap-2"
                      onClick={() => downloadUnmatchedExcel(unmatchedList, verifiedData.invoice_no)}
                    >
                      <FileSpreadsheet size={18} />
                      Export Unmatched Excel
                    </button>
                  )}

                  <button className="btn btn-outline-secondary btn-lg py-2.5" onClick={reset}>
                    Cancel & Reset
                  </button>
                </div>
              </div>
            )}

            {isProcessing && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status"></div>
                <h5 className="fw-bold">Processing Invoice...</h5>
                <p className="text-muted">Extracting data and linking products with inventory records</p>
              </div>
            )}

            {error && (
              <div className="alert alert-danger mt-4 d-flex align-items-center gap-2">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Import Result Screen */}
            {result && (
              <div className="mt-2">
                <div className="alert alert-success d-flex align-items-center justify-content-between p-3 rounded-3">
                  <div className="d-flex align-items-center gap-2">
                    <CheckCircle size={24} className="text-success" />
                    <div>
                      <h5 className="fw-bold mb-0">Import Completed Successfully!</h5>
                      <small>Invoice: <strong>{result.invoice_no || '-'}</strong> | Purchase Record ID: <strong>#{result.purchase_id || '-'}</strong></small>
                    </div>
                  </div>

                  {result.unmatched_items?.length > 0 && (
                    <button
                      className="btn btn-warning btn-sm fw-bold d-flex align-items-center gap-1 shadow-sm"
                      onClick={() => downloadUnmatchedExcel(result.unmatched_items, result.invoice_no)}
                    >
                      <FileSpreadsheet size={16} />
                      Download Unmatched Products Excel ({result.unmatched_items.length})
                    </button>
                  )}
                </div>

                <div className="row g-3 mt-2 text-center">
                  <div className="col-md-3">
                    <div className="card border-0 bg-light p-3 rounded-3">
                      <div className="text-muted small">Total Found in File</div>
                      <h4 className="fw-bold mb-0">{result.total_found_in_pdf || result.total_found_in_file || result.items_imported || 0}</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-light p-3 rounded-3">
                      <div className="text-muted small">Imported to Purchases</div>
                      <h4 className="fw-bold mb-0 text-primary">{result.items_imported || 0}</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-light p-3 rounded-3">
                      <div className="text-muted small">Matched in Database</div>
                      <h4 className="fw-bold mb-0 text-success">{result.items_matched_in_db || 0}</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-light p-3 rounded-3">
                      <div className="text-muted small">Unmatched / Created</div>
                      <h4 className="fw-bold mb-0 text-warning">{result.items_unmatched_in_db || result.failed_to_match?.length || 0}</h4>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-end">
                  <button className="btn btn-primary px-4 fw-bold" onClick={reset}>
                    Import Another Invoice
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Interactive Product Search & Selection Modal */}
      {editingMatchItem && (
        <Modal
          show={!!editingMatchItem}
          onClose={() => setEditingMatchItem(null)}
          heading={`Select Database Product (Item #${editingMatchItem.index + 1})`}
        >
          <div className="p-1">
            {/* Invoice Line Banner */}
            <div className="alert alert-light border rounded-3 p-3 mb-3">
              <div className="text-secondary small fw-semibold">Invoice Line Details:</div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-1">
                <div>
                  <h6 className="fw-bold text-dark mb-0">{editingMatchItem.item.name}</h6>
                  <small className="text-muted">
                    Potency: <strong>{editingMatchItem.item.potency || 'Standard'}</strong> | Vol: <strong>{editingMatchItem.item.volume || 'Standard'}</strong> | Batch: <strong>{editingMatchItem.item.batch_no || '-'}</strong>
                  </small>
                </div>
                {editingMatchItem.item.matched && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                    onClick={() => handleClearProductMatch(editingMatchItem.index)}
                  >
                    <Unlink size={13} />
                    Unlink (Mark as Blank)
                  </button>
                )}
              </div>
            </div>

            {/* Search Box */}
            <div className="mb-3">
              <label className="form-label fw-bold text-dark small mb-1">
                Search Product in Database (by Name, SKU, Code):
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light"><Search size={16} /></span>
                <input
                  type="text"
                  className="form-control fw-semibold"
                  placeholder="Type product name or SKU..."
                  value={productSearchQuery}
                  onChange={(e) => {
                    setProductSearchQuery(e.target.value);
                    fetchProductSearchResults(e.target.value);
                  }}
                  autoFocus
                />
                {productSearchQuery && (
                  <button className="btn btn-outline-secondary" onClick={() => { setProductSearchQuery(''); fetchProductSearchResults(''); }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Search Results Table */}
            <div className="table-responsive border rounded-3" style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light sticky-top shadow-sm">
                  <tr>
                    <th>Product Name & SKU</th>
                    <th>Brand</th>
                    <th>Variants Available</th>
                    <th className="text-center" style={{ width: '110px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isSearchingProducts ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        <div className="spinner-border spinner-border-sm me-2 text-primary" role="status"></div>
                        Searching database products...
                      </td>
                    </tr>
                  ) : productSearchResults.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        No products found matching "{productSearchQuery}".
                      </td>
                    </tr>
                  ) : (
                    productSearchResults.map((prod) => {
                      const isCurrent = editingMatchItem.item.product_id === prod.id;
                      return (
                        <tr key={prod.id} className={isCurrent ? 'table-success' : ''}>
                          <td>
                            <div className="fw-bold text-dark">{prod.name}</div>
                            <div className="d-flex gap-1 align-items-center mt-1">
                              <span className="badge bg-secondary-subtle text-secondary border font-monospace" style={{ fontSize: '10px' }}>
                                SKU: {prod.sku || 'N/A'}
                              </span>
                              <span className="badge bg-info-subtle text-info border font-monospace" style={{ fontSize: '10px' }}>
                                ID: #{prod.id}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-dark text-white" style={{ fontSize: '11px' }}>
                              {prod.brand_name || 'Brand'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {prod.variants?.length > 0 ? (
                                prod.variants.map((v, i) => (
                                  <span key={i} className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                                    {v.label}
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted small">Standard</span>
                              )}
                            </div>
                          </td>
                          <td className="text-center">
                            {isCurrent ? (
                              <span className="badge bg-success text-white px-2 py-1">
                                <Check size={12} className="me-1" />
                                Current
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary px-3 fw-bold shadow-sm"
                                onClick={() => handleSelectProductMatch(editingMatchItem.index, prod)}
                              >
                                Link
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 fw-semibold"
                onClick={() => setEditingMatchItem(null)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Auto-Linked Products Log Modal */}
      {showLinksLogModal && (
        <Modal
          size="xl"
          title="Auto-Linked Products Log"
          show={showLinksLogModal}
          handleClose={() => setShowLinksLogModal(false)}
        >
          <div className="p-3">
            <div className="alert alert-light border d-flex align-items-center gap-3 mb-3">
              <BookmarkCheck size={28} className="text-primary flex-shrink-0" />
              <div>
                <div className="fw-bold text-dark">Learned Product Link Mappings</div>
                <small className="text-muted">
                  These products were previously linked during your purchase imports. Whenever an uploaded invoice contains matching names or codes, the system automatically pre-links them to your inventory.
                </small>
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="row g-2 mb-3">
              <div className="col-12 col-md-7">
                <div className="input-group">
                  <span className="input-group-text bg-white"><Search size={15} /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by invoice item name, linked DB product, or code..."
                    value={linksLogSearch}
                    onChange={(e) => {
                      setLinksLogSearch(e.target.value);
                      fetchLinksLogList(1, e.target.value, linksLogVendorFilter);
                    }}
                  />
                  {linksLogSearch && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setLinksLogSearch('');
                        fetchLinksLogList(1, '', linksLogVendorFilter);
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-5">
                <select
                  className="form-select"
                  value={linksLogVendorFilter}
                  onChange={(e) => {
                    setLinksLogVendorFilter(e.target.value);
                    fetchLinksLogList(1, linksLogSearch, e.target.value);
                  }}
                >
                  <option value="all">All Vendors / Importers</option>
                  <option value="reck">Reckeweg</option>
                  <option value="wsi">WSI (Schwabe)</option>
                  <option value="modern">Modern Homoeo</option>
                  <option value="mani">Mani Enterprise</option>
                  <option value="standard">Other / Standard Local</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="table-responsive border rounded-3" style={{ maxHeight: '420px', overflowY: 'auto' }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light sticky-top shadow-sm">
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th style={{ minWidth: '220px' }}>Import Invoice Item Name</th>
                    <th style={{ minWidth: '220px' }}>Linked System Product</th>
                    <th style={{ width: '130px' }}>Vendor</th>
                    <th style={{ width: '130px' }}>Category</th>
                    <th style={{ width: '140px' }}>Last Updated</th>
                    <th className="text-center" style={{ width: '80px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {linksLogLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm me-2 text-primary" role="status"></div>
                        Loading linked products log...
                      </td>
                    </tr>
                  ) : linksLogList.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        <div className="mb-2"><Link size={24} className="text-muted opacity-50" /></div>
                        No linked products recorded yet. When you link products in any invoice verification, they will be logged here automatically.
                      </td>
                    </tr>
                  ) : (
                    linksLogList.map((logItem, idx) => (
                      <tr key={logItem.id}>
                        <td className="text-muted small fw-bold">{(linksLogPage - 1) * 50 + idx + 1}</td>
                        <td>
                          <div className="fw-bold text-dark">{logItem.import_item_name}</div>
                          {logItem.import_item_code && (
                            <span className="badge bg-light text-secondary border font-monospace mt-1" style={{ fontSize: '10px' }}>
                              Code: {logItem.import_item_code}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="fw-semibold text-success">{logItem.product_name}</div>
                          <span className="badge bg-success-subtle text-success border border-success-subtle font-monospace mt-1" style={{ fontSize: '10px' }}>
                            Product ID: #{logItem.product_id}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-secondary-subtle text-dark border">
                            {logItem.vendor_name || logItem.vendor_key || 'General'}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info-subtle text-info border">
                            {logItem.category_name || '-'}
                          </span>
                        </td>
                        <td className="text-muted small">
                          {logItem.updatedAt ? new Date(logItem.updatedAt).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          }) : '-'}
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger p-1 shadow-sm"
                            title="Remove link from log"
                            onClick={() => handleDeleteLinkLog(logItem.id, logItem.import_item_name)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Showing {linksLogList.length} of {linksLogTotal} logged product links
              </small>
              <button
                type="button"
                className="btn btn-secondary px-4 fw-semibold"
                onClick={() => setShowLinksLogModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PurchaseImportPage;
